import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import PreconisationIAZone from "@/components/PreconisationIAZone";
import BenchmarkMarche from "@/components/BenchmarkMarche";
import PerformanceCommerciale from "@/components/PerformanceCommerciale";
import KpiAuditModal from "@/components/KpiAuditModal";
import UsageCard from "@/components/UsageCard";
import BriefingStrategiqueButton from "@/components/BriefingStrategiqueButton";
import { api, formatEUR } from "@/lib/api";
import { trackOnce, track, USAGE_EVENTS } from "@/lib/usage";
import {
  Target,
  TrendUp,
  Lightning,
  Warning,
  Fire,
  ArrowRight,
  Compass,
  ChartLineUp,
  Calendar,
  UsersThree,
  CheckCircle,
} from "@phosphor-icons/react";

// ============================================================================
// V4.12 — Pilotage orienté ATTEINTE D'OBJECTIF (et non analyse de stock).
// Une seule question : "Que dois-je faire aujourd'hui pour atteindre mon
// objectif de fin de mois ?"
// ============================================================================

const STAGE_LABELS = {
  prospect: "Prospect",
  compromis: "Compromis",
  envoi_banque: "Envoi banque",
  accord_banque: "Accord banque",
  accord_risque: "Accord risque",
  rdv_bancaire: "RDV bancaire",
  offre_editee: "Offre éditée",
  acte_authentique: "Acte authentique",
};

// ---- Tokens UI ----
const sevPalette = (sev) =>
  ({
    critique: { bg: "var(--cf-danger-soft)", fg: "var(--cf-danger-text)", accent: "var(--cf-danger)", overline: "var(--cf-danger-text)" },
    alerte: { bg: "var(--cf-warning-soft)", fg: "var(--cf-warning-text)", accent: "var(--cf-warning)", overline: "var(--cf-warning-text)" },
    ok: { bg: "var(--cf-success-soft)", fg: "var(--cf-success-text)", accent: "var(--cf-success)", overline: "var(--cf-success-text)" },
  }[sev] || { bg: "var(--cf-primary-soft)", fg: "var(--cf-primary-hover)", accent: "var(--cf-primary)", overline: "var(--cf-primary-hover)" });

// Petite KPI carte (réutilisable). value en gros, libellé en overline.
function KpiCell({ label, value, suffix, tone = "neutral", testid }) {
  const colors = {
    neutral: "var(--cf-text)",
    danger: "var(--cf-danger-text)",
    warning: "var(--cf-warning-text)",
    success: "var(--cf-success-text)",
    primary: "var(--cf-primary-hover)",
  };
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="overline mb-1">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-extrabold tabular-nums" style={{ color: colors[tone] }}>
        {value}
        {suffix && <span className="text-base font-bold text-slate-400 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, color, children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} weight="fill" color={color} />
      <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">{children}</h2>
    </div>
  );
}

export default function PilotageStrategique() {
  const [loading, setLoading] = useState(true);
  const [obj, setObj] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const navigate = useNavigate();

  const fetchObj = () => {
    return api
      .get("/pilotage/objectif")
      .then((r) => setObj(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchObj();
    // V4.36 — Tracking ouverture page (dédup 30 min)
    trackOnce(USAGE_EVENTS.PILOTAGE_OPEN, "pilotage_page_open", 30);
    // V4.18 — Refresh quand la page redevient visible (cas : user édite une fiche
    // dans un autre onglet ou via drawer puis revient sur Pilotage)
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchObj();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, []);

  if (loading || !obj) return <Layout title="Pilotage"><div className="card-base h-96 animate-pulse" /></Layout>;

  const om = obj.objectif_mensuel;
  const proj = obj.projection;
  const mc = obj.machine_commerciale;
  const disc = obj.discipline;
  const pu = obj.priorite_unique;
  const sev = sevPalette(pu.severity);

  // ---- Helpers UI ----
  const ecartFmt = proj.ecart_objectif > 0 ? `+${proj.ecart_objectif}` : `${proj.ecart_objectif}`;
  // V4.31 — P0-1 : "Indice de rythme" (ex-"Probabilité d'atteinte"). Ce n'est pas une probabilité.
  // P0-3 : on affiche la valeur brute si > 100 (ex : 145 = +45% au-dessus du rythme requis).
  const indice = proj.indice_rythme ?? proj.probabilite_atteinte ?? 0;
  const indiceRaw = proj.indice_rythme_raw ?? indice;
  const indiceTone = indice >= 80 ? "success" : indice >= 50 ? "warning" : "danger";
  const indiceColor = indiceTone === "success" ? "var(--cf-success)" : indiceTone === "warning" ? "var(--cf-warning)" : "var(--cf-danger)";
  // P0-4 : flag volatilité (trop tôt pour projeter)
  const projVolatile = !!proj.volatile;
  const projVolatileReason = proj.volatile_reason || "Trop tôt pour projeter";
  // V4.33 — P1-7 : sample size + indicateur de fiabilité (3 niveaux).
  const projSampleSize = proj.sample_size ?? 0;
  const projFiabilite = proj.fiabilite || "faible";
  const projFiabiliteLabel = proj.fiabilite_label || "Fiabilité faible";
  const fiabiliteColor =
    projFiabilite === "elevee" ? "var(--cf-success)" :
    projFiabilite === "moyenne" ? "var(--cf-warning)" : "var(--cf-text-3)";
  const fiabiliteDots = projFiabilite === "elevee" ? 3 : projFiabilite === "moyenne" ? 2 : 1;

  // Progress bar mandats
  const pctMandats =
    om.mandats_objectif > 0 ? Math.min(100, Math.round((om.mandats_realises / om.mandats_objectif) * 100)) : 0;

  return (
    <Layout
      title="Pilotage"
      subtitle={
        <span className="flex items-center gap-1.5">
          <Compass size={12} weight="fill" color="var(--cf-primary)" /> Atteinte d'objectif
        </span>
      }
    >
      <div className="max-w-3xl mx-auto" data-testid="pilotage-objectif-root">
        {/* En-tête desktop */}
        <div className="hidden md:block mb-6">
          <div className="overline mb-1">Pilotage</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900">Objectif</h1>
          <p className="text-slate-500 mt-1">Que dois-je faire aujourd'hui pour atteindre l'objectif du mois ?</p>
        </div>

        {/* V4.42 — Bouton "Copier briefing stratégique" pour ChatGPT */}
        <div className="mb-4 flex justify-end" data-testid="briefing-button-wrapper-pilotage">
          <BriefingStrategiqueButton variant="primary" testid="briefing-copy-btn-pilotage" />
        </div>

        {/* Snapshot Copilot IA — accessoire en haut */}
        <div className="mb-4" data-testid="copilot-snapshot-wrapper">
          <PreconisationIAZone />
        </div>

        {/* ====================================================== */}
        {/* 1. PRIORITÉ UNIQUE — bandeau dominant                  */}
        {/* ====================================================== */}
        <div
          className="rounded-2xl p-6 mb-5"
          style={{ background: sev.bg }}
          data-testid="priorite-unique-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={16} weight="fill" color={sev.accent} />
            <div className="overline" style={{ color: sev.overline }}>
              Priorité unique
            </div>
          </div>
          <p
            className="font-display text-xl md:text-3xl font-extrabold leading-tight tracking-tight"
            style={{ color: sev.fg }}
            data-testid="priorite-unique-label"
          >
            {pu.label}
          </p>
          <p
            className="font-display text-sm md:text-base font-bold mt-2 leading-snug"
            style={{ color: sev.fg, opacity: 0.85 }}
            data-testid="priorite-unique-action"
          >
            {pu.action}
          </p>
        </div>

        {/* ====================================================== */}
        {/* 2. OBJECTIF MENSUEL                                    */}
        {/* ====================================================== */}
        <section className="card-base p-5 mb-4" data-testid="objectif-mensuel-card">
          <SectionTitle icon={Target} color="var(--cf-primary)">Objectif mensuel</SectionTitle>
          <div className="flex items-center gap-4 mb-3">
            <KpiCell
              label="Mandats objectif"
              value={om.mandats_objectif}
              testid="kpi-mandats-objectif"
            />
            <button
              type="button"
              onClick={() => { track(USAGE_EVENTS.PILOTAGE_AUDIT_OPEN); setAuditOpen(true); }}
              className="flex-1 min-w-0 text-left rounded-lg hover:bg-slate-50 -mx-1 px-1 py-0.5 transition-colors group"
              data-testid="kpi-mandats-realises-audit-btn"
              title="Voir les dossiers qui composent ce chiffre"
            >
              <div className="overline mb-1 flex items-center gap-1">
                Réalisés
                <span className="text-[9px] font-extrabold text-[color:var(--cf-primary-hover)] opacity-0 group-hover:opacity-100 transition-opacity uppercase">
                  · auditer
                </span>
              </div>
              <div
                className="font-display text-2xl md:text-3xl font-extrabold tabular-nums group-hover:underline decoration-2 decoration-[color:var(--cf-success-border)] underline-offset-4"
                style={{ color: "var(--cf-success-text)" }}
              >
                {om.mandats_realises}
              </div>
            </button>
            <KpiCell
              label="Manquants"
              value={om.mandats_manquants}
              tone={om.mandats_manquants > 0 ? "danger" : "success"}
              testid="kpi-mandats-manquants"
            />
          </div>
          {/* Barre de progression mandats */}
          <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden" data-testid="mandats-progress">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${pctMandats}%`,
                background: pctMandats >= 100 ? "var(--cf-success)" : "var(--cf-primary)",
              }}
            />
          </div>
          <div className="text-[11px] text-slate-400 font-bold mt-1.5 tabular-nums">
            {pctMandats}% du mois — {obj.month}
          </div>
        </section>

        {/* ====================================================== */}
        {/* 3. PROJECTION                                          */}
        {/* ====================================================== */}
        <section className="card-base p-5 mb-4" data-testid="projection-card">
          <SectionTitle icon={ChartLineUp} color="var(--cf-accent)">Projection</SectionTitle>

          {/* V4.31 — P0-4 : badge "Trop tôt pour projeter" quand échantillon insuffisant */}
          {projVolatile && (
            <div
              className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200"
              data-testid="projection-volatile-badge"
            >
              <Warning size={14} weight="fill" color="var(--cf-text-2)" />
              <span className="text-[12px] font-bold text-slate-600">
                Trop tôt pour projeter
              </span>
              <span className="text-[11px] text-slate-500 font-semibold">— {projVolatileReason}</span>
            </div>
          )}

          <div className="flex items-center gap-4">
            <KpiCell
              label="Projection fin de mois"
              value={proj.projection_fin_mois}
              testid="kpi-projection-fin-mois"
            />
            <KpiCell
              label="Écart objectif"
              value={ecartFmt}
              tone={proj.ecart_objectif >= 0 ? "success" : "danger"}
              testid="kpi-ecart-objectif"
            />
            {/* V4.31 — P0-1 + P0-3 : "Indice de rythme" (ex-Probabilité), valeur brute si > 100 */}
            <KpiCell
              label="Indice de rythme"
              value={indiceRaw > 100 ? indiceRaw : indice}
              suffix="%"
              tone={indiceTone}
              testid="kpi-indice-rythme"
            />
          </div>
          {/* Jauge indice de rythme (capée à 100 visuellement) */}
          <div className="mt-4 w-full h-2 rounded-full bg-slate-100 overflow-hidden" data-testid="indice-rythme-bar">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${indice}%`, background: indiceColor }}
            />
          </div>
          <div className="text-[11px] text-slate-400 font-bold mt-1.5" data-testid="indice-rythme-caption">
            {projVolatile
              ? "Indice non significatif tant que le mois démarre."
              : indiceRaw > 100
              ? `+${indiceRaw - 100}% d'avance sur le rythme requis.`
              : indice >= 80
              ? "Rythme aligné sur l'objectif."
              : indice >= 50
              ? "Rythme insuffisant — accélération requise."
              : "Rythme critique — refonte du plan nécessaire."}
          </div>

          {/* V4.33 — P1-7 : Sample size + indicateur de fiabilité (dots) */}
          <div
            className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2"
            data-testid="projection-fiabilite-block"
          >
            <div className="flex items-center gap-2 text-[11px] text-slate-500 font-semibold">
              <span data-testid="projection-sample-size">
                <span className="tabular-nums font-bold text-slate-700">{projSampleSize}</span>{" "}
                mandat{projSampleSize > 1 ? "s" : ""} signé{projSampleSize > 1 ? "s" : ""} ce mois ·
              </span>
              <span data-testid="projection-jours-ouvres">
                <span className="tabular-nums font-bold text-slate-700">{proj.jours_ouvres_ecoules ?? 0}</span>
                <span className="text-slate-400">/{proj.jours_ouvres_total ?? 22}</span> jours ouvrés
              </span>
            </div>
            <div
              className="flex items-center gap-1.5"
              title={projFiabiliteLabel}
              data-testid={`projection-fiabilite-${projFiabilite}`}
            >
              <span className="flex items-center gap-0.5">
                {[1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: i <= fiabiliteDots ? fiabiliteColor : "var(--cf-border)" }}
                  />
                ))}
              </span>
              <span
                className="text-[10px] font-extrabold uppercase tracking-wider tabular-nums"
                style={{ color: fiabiliteColor }}
              >
                {projFiabiliteLabel}
              </span>
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 4. MACHINE COMMERCIALE                                 */}
        {/* ====================================================== */}
        <section className="card-base p-5 mb-4" data-testid="machine-commerciale-card">
          <SectionTitle icon={TrendUp} color="var(--cf-info)">Machine commerciale</SectionTitle>
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            <div data-testid="kpi-ratio-contacts-mandats">
              <div className="overline mb-1">Ratio contacts → mandats</div>
              <div className="font-display text-2xl font-extrabold tabular-nums text-slate-900">
                {mc.ratio_contacts_mandats}
                <span className="text-sm font-bold text-slate-400 ml-1">: 1</span>
              </div>
              <div className="text-[11px] text-slate-400 font-semibold mt-0.5">
                {mc.ratio_source === "historique_90j"
                  ? `Historique 90j · ${mc.ratio_sample_size} mandat(s)`
                  : "Ratio cible (peu d'historique)"}
              </div>
            </div>
            <div data-testid="kpi-contacts-necessaires">
              <div className="overline mb-1">Contacts nécessaires</div>
              <div className="font-display text-2xl font-extrabold tabular-nums text-slate-900">
                {mc.contacts_necessaires}
              </div>
              <div className="text-[11px] text-slate-400 font-semibold mt-0.5">
                Restants pour combler {om.mandats_manquants} mandat(s)
              </div>
            </div>
            <div data-testid="kpi-objectif-quotidien">
              <div className="overline mb-1">Objectif quotidien</div>
              <div className="font-display text-2xl font-extrabold tabular-nums" style={{ color: "var(--cf-primary-hover)" }}>
                {mc.objectif_quotidien_contacts}
                <span className="text-sm font-bold text-slate-400 ml-1">/jour</span>
              </div>
              <div className="text-[11px] text-slate-400 font-semibold mt-0.5">
                Contacts à créer chaque jour
              </div>
            </div>
            <div data-testid="kpi-jours-restants">
              <div className="overline mb-1">Jours restants</div>
              <div className="font-display text-2xl font-extrabold tabular-nums text-slate-900">
                {mc.jours_restants}
                <span className="text-sm font-bold text-slate-400 ml-1">/ {mc.jours_total_mois}</span>
              </div>
              <div className="text-[11px] text-slate-400 font-semibold mt-0.5">
                Mois écoulé à {Math.round((mc.jours_ecoules / mc.jours_total_mois) * 100)}%
              </div>
            </div>
          </div>
          {/* Petite ligne info contacts déjà créés ce mois */}
          <div className="mt-3 px-3 py-2 rounded-lg bg-[color:var(--cf-primary-soft)] text-[12px] font-semibold text-[color:var(--cf-primary-hover)]">
            {mc.contacts_realises_mois} contact(s) déjà créé(s) ce mois — continue le rythme.
          </div>
        </section>

        {/* ====================================================== */}
        {/* 5. DISCIPLINE OPÉRATIONNELLE                           */}
        {/* ====================================================== */}
        <section className="card-base p-5 mb-6" data-testid="discipline-card">
          <SectionTitle icon={Fire} color="var(--cf-danger)">Discipline opérationnelle</SectionTitle>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => navigate("/dossiers/en-cours")}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left border border-slate-100"
              data-testid="discipline-relances-retard"
            >
              <div className="w-10 h-10 rounded-xl bg-[color:var(--cf-danger-soft)] flex items-center justify-center flex-shrink-0">
                <Warning size={18} weight="fill" color="var(--cf-danger)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[14px] text-slate-900">Relances en retard</div>
                <div className="text-[11px] text-slate-500 font-semibold">À rattraper avant tout nouveau contact</div>
              </div>
              <div className="font-display text-2xl font-extrabold tabular-nums" style={{ color: disc.relances_retard > 0 ? "var(--cf-danger)" : "var(--cf-text-3)" }}>
                {disc.relances_retard}
              </div>
              <ArrowRight size={14} className="text-slate-300 flex-shrink-0" />
            </button>

            <button
              type="button"
              onClick={() => navigate("/dossiers/en-cours")}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left border border-slate-100"
              data-testid="discipline-sans-action"
            >
              <div className="w-10 h-10 rounded-xl bg-[color:var(--cf-warning-soft)] flex items-center justify-center flex-shrink-0">
                <Calendar size={18} weight="fill" color="var(--cf-warning)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[14px] text-slate-900">Dossiers sans action</div>
                <div className="text-[11px] text-slate-500 font-semibold">Aucune relance depuis &gt; 14 jours</div>
              </div>
              <div className="font-display text-2xl font-extrabold tabular-nums" style={{ color: disc.dossiers_sans_action > 0 ? "var(--cf-warning)" : "var(--cf-text-3)" }}>
                {disc.dossiers_sans_action}
              </div>
              <ArrowRight size={14} className="text-slate-300 flex-shrink-0" />
            </button>

            <div
              className="p-3 rounded-xl border border-slate-100"
              data-testid="discipline-chauds"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[color:var(--cf-success-soft)] flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={18} weight="fill" color="var(--cf-success)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[14px] text-slate-900">Dossiers chauds</div>
                  <div className="text-[11px] text-slate-500 font-semibold">Accord banque → offre éditée</div>
                </div>
                <div className="font-display text-2xl font-extrabold tabular-nums" style={{ color: disc.dossiers_chauds_a_traiter > 0 ? "var(--cf-success)" : "var(--cf-text-3)" }}>
                  {disc.dossiers_chauds_a_traiter}
                </div>
              </div>
              {/* Top 3 chauds — cliquables */}
              {disc.chauds_preview && disc.chauds_preview.length > 0 && (
                <ul className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid="chauds-preview-list">
                  {disc.chauds_preview.map((d) => (
                    <li
                      key={d.id}
                      onClick={() => navigate(`/dossiers/${d.id}`)}
                      className="flex items-center justify-between text-[12.5px] cursor-pointer hover:bg-slate-50 px-2 py-1.5 -mx-2 rounded-lg transition-colors"
                      data-testid={`chaud-row-${d.id}`}
                    >
                      <span className="font-semibold text-slate-900 truncate flex-1 mr-2">{d.nom}</span>
                      <span className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400 mr-2">
                        {STAGE_LABELS[d.etape] || d.etape}
                      </span>
                      <span className="font-bold tabular-nums text-slate-700">{formatEUR(d.ca_net)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* ====================================================== */}
        {/* 6. PERFORMANCE VS MARCHÉ — preuve commerciale          */}
        {/* ====================================================== */}
        <BenchmarkMarche />

        {/* ====================================================== */}
        {/* 7. PERFORMANCE COMMERCIALE — V4.15                     */}
        {/* "Machine à produire de la confiance"                   */}
        {/* ====================================================== */}
        <PerformanceCommerciale />

        {/* ====================================================== */}
        {/* V4.36 — Analytics d'usage interne                       */}
        {/* "Comprendre comment j'utilise réellement l'app"        */}
        {/* ====================================================== */}
        <UsageCard />

        {/* Lien Apporteurs — discret */}
        <button
          type="button"
          onClick={() => navigate("/apporteurs")}
          className="w-full card-base p-4 flex items-center gap-3 hover:border-slate-300 hover:shadow-sm transition-all text-left"
          data-testid="pilotage-link-apporteurs"
        >
          <div className="w-10 h-10 rounded-xl bg-[color:var(--cf-primary-soft)] flex items-center justify-center flex-shrink-0">
            <UsersThree size={20} weight="fill" color="var(--cf-primary)" />
          </div>
          <div className="flex-1">
            <div className="font-display text-base font-extrabold text-slate-900">Apporteurs</div>
            <div className="text-xs text-slate-500 font-semibold">Performance et CA par apporteur</div>
          </div>
          <ArrowRight size={16} weight="bold" className="text-slate-400" />
        </button>
      </div>

      {/* V4.18 — Modale d'audit du KPI Mandats réalisés */}
      <KpiAuditModal
        open={auditOpen}
        onClose={() => {
          setAuditOpen(false);
          // Re-fetch après fermeture au cas où l'utilisateur aurait modifié une fiche depuis la modale
          fetchObj();
        }}
        kpi="mandats_realises"
        title={`Mandats réalisés — ${obj.month}`}
        onOpenDossier={(it) => {
          if (it?.id) navigate(`/dossiers/${it.id}`);
        }}
      />
    </Layout>
  );
}
